import {styled} from '@mui/material/styles';
import Tab, {tabClasses} from '@mui/material/Tab';
import TabList from '@mui/lab/TabList';
import {tabsClasses} from '@mui/material/Tabs';

/**
 * Boxed tab style — outlined "folder tabs" that connect to a content panel
 * beneath them. The selected tab fills white and rounds its top corners;
 * unselected tabs sit on a muted gray background.
 *
 * Pair with BoxedTabList and a @mui/lab TabContext:
 *
 *   <TabContext value={tab}>
 *     <BoxedTabList onChange={(e, v) => setTab(v)}>
 *       <BoxedTab value="a" label="A" />
 *       <BoxedTab value="b" label="B" />
 *     </BoxedTabList>
 *   </TabContext>
 *
 * Supports `size="small"` for compact placements (e.g. inside a dashboard
 * card toolbar).
 */
export const BoxedTab = styled(Tab, {shouldForwardProp: prop => prop !== 'size'})(({theme, size = 'medium'}) => ({
    border: `1px solid ${(theme.vars || theme).palette.divider}`,
    marginRight: 0,
    marginBottom: 0,
    marginTop: 4,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderRadius: 0,
    opacity: 0.8,
    background: '#f7f7f7',
    ...(size === 'small' && {
        fontSize: '0.75rem',
        minHeight: 32,
        padding: '0 8px'
    }),
    [`&.${tabClasses.selected}`]: {
        background: '#ffffff',
        opacity: 1,
        fontWeight: 'bold',
        marginTop: 0,
        borderRightWidth: 1,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        '& + *': {
            borderLeftWidth: 0
        }
    },
    '& .MuiTab-icon:hover': {
        background: 'transparent'
    },
    '&:first-of-type': {
        borderTopLeftRadius: 4
    },
    '&:not(:has(~ .MuiTab-root))': {
        borderTopRightRadius: 4,
        borderRightWidth: 1
    }
}));

export const BoxedTabList = styled(TabList, {shouldForwardProp: prop => prop !== 'size'})(({size = 'medium'}) => ({
    minHeight: size === 'small' ? 32 : 30,
    [`& .${tabsClasses.indicator}`]: {
        display: 'none'
    }
}));
